import { ApolloClient, gql } from 'apollo-boost';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import {
  FeedbackFilter,
  FeedbackFilters,
} from 'shared/components/Feedback/FeedbackDashboardFilterBar';
import { ApplyFilters } from 'shared/components/Feedback/FiltersDropdown/ShowFilter';
import { Contact } from './Feedback/utils';
import { Report } from 'shared/components/WelcomeBack/utils';
import { ReportingType } from 'npo/pages/ReportsBeta/NewReport/api';

const query = (reportId: number | string | undefined) => gql`
  query useFeedback(
    $top: Int
    $offset: Int
    $sortBy: String
    $filters: [ReportsOmniSearch!]
    ${reportId ? '$reportId: ID' : ''}
  ) {
    reportsTodo(
      first: $top
      offset: $offset
      sortBy: $sortBy
      filters: {${reportId ? ' id: $reportId,' : ''} or: $filters }
    ) {
      edges {
        node {
          dbId
          name
          version
          stage
          status
          reportingType
          reportId
          endDate
          year
          feedbackType
          fiscalYearStart
          fiscalYearEnd
          programId
          sponsorName
          startDate
          tasks
          errors
          warnings
          totalDonations
          totalCost
          accountName
          updatedAt
          createdAt
          npoName
          donorName
          programName
          contactNpo
          contactDonor
          portfolioName
          relationshipManagerId
          relationshipManagerEmail
          relationshipManagerFirstName
          relationshipManagerLastName
          isAllocated
          claim
          cnOptedIn
          investmentType
          budget
          totalBudgetInitial
          totalBudgetVariance
          budgetNote1
          budgetNote1Date
          budgetNote2
          budgetNote2Date
          aditionalBudgetNotes
          directCashVariance
          indirectCashVariance
          directInkindVariance
          indirectInkindVariance
          optedInFullReporting
          feedbacks {
            edges {
              node {
                granteeText
                adminText
                funderText
              }
            }
          }
          allocations { edges { node { dbId investment { dbId }}}}
        }
      }
    }
  }
`;

export type ReportStage = 'INITIAL' | 'INTERIM' | 'FINAL';
export const ReportStages: ReportStage[] = ['INITIAL', 'INTERIM', 'FINAL'];

export type FeedbackStatus =
  | 'Draft'
  | 'Interim Updates Needed'
  | 'Final Updates Needed'
  | 'Awaiting TI Feedback'
  | 'TI Feedback Provided'
  | 'Awaiting TI Verification'
  | 'Awaiting Funder Feedback'
  | 'Funder Feedback Provided'
  | 'Awaiting Funder Verification'
  | 'Published'
  | 'Finalized'; // it could be finished

export type ReportStatus = FeedbackStatus;

export const ReportStatuses: FeedbackStatus[] = [
  'Draft',
  'Interim Updates Needed',
  'Final Updates Needed',
  'Awaiting TI Feedback',
  'TI Feedback Provided',
  'Awaiting TI Verification',
  'Awaiting Funder Feedback',
  'Funder Feedback Provided',
  'Awaiting Funder Verification',
  'Published',
  'Finalized',
];

export interface FeedbackApi extends Report {
  contactDonor: string; // "flevy+funder2@trueimpact.com"
  contactNpo: string; // "ehilberg+runscope+grantee+9837@mg.trueimpact.com"
  dbId: number; // 3704
  reportId: number; // 3704
  donorName: string; // "ABC Funder"
  endDate: string; // "2019-02-17"
  feedbacks: {
    granteeText: string | null;
    adminText: string | null;
    funderText: string | null;
  }[];
  name: string; // "TestReportQAI_01"
  npoName: string; // "Let's Thrive!"
  portfolioName: string; // "Education"
  programName: string;
  relationshipManagerEmail: string | null;
  relationshipManagerFirstName: string | null;
  relationshipManagerId: number | null;
  relationshipManagerLastName: string | null;
  status: FeedbackStatus;
  stage: ReportStage;
  updatedAt: string | null; // "2021-11-10T15:55:16"
  year: string; // "2019"
  totalDonations: number; // 105594.1
  totalCost: number;
  accountName: string | null;
  claim: number;
  cnOptedIn: boolean | null;
  investmentType: null;
  budget: number;
  totalBudgetInitial: number;
  totalBudgetVariance: number;
  budgetNote1: string | null;
  budgetNote1Date: string | null;
  budgetNote2: string | null;
  budgetNote2Date: string | null;
  directCashVariance: number;
  indirectCashVariance: number;
  directInkindVariance: number;
  indirectInkindVariance: number;
  reportingType: ReportingType | null;
  optedInFullReporting: boolean | null;
  version: 'v4' | null;
}

interface FeedbackMapping {
  feedbackRows: FeedbackDashboardCardProps[];
}

const getFeedbackContacts = (
  contacts: FeedbackApi['feedbacks'],
  donorContact: string | null,
  npoContact: string | null,
  relationshipManagerEmail: string | null,
): Contact[] => {
  const result: Contact[] = [];
  let npoIsNotSet = true;
  let donorIsNotSet = true;
  contacts.forEach(c => {
    c.adminText &&
      result.push({
        name: c.adminText,
        email: undefined, // TODO: add TI admin contact info in the API
        accountType: 'ti staff',
      });
    if (c.funderText) {
      donorIsNotSet = false;
      result.push({
        name: c.funderText,
        email: relationshipManagerEmail || undefined,
        accountType: 'donor',
      });
    }
    if (c.granteeText) {
      npoIsNotSet = false;
      result.push({
        name: c.granteeText,
        email: npoContact || undefined,
        accountType: 'npo',
      });
    }
  });

  if (npoIsNotSet) {
    result.push({
      name: '',
      email: npoContact || undefined,
      accountType: 'npo',
    });
  }

  if (donorIsNotSet) {
    result.push({
      name: '',
      email: relationshipManagerEmail || undefined,
      accountType: 'donor',
    });
  }

  return result;
};

export const feedbackMapFromApi = (
  response: FeedbackApi[],
): FeedbackMapping => {
  const feedbackRows: FeedbackDashboardCardProps[] = [];
  response.forEach(element => {
    const now = new Date().toISOString();
    feedbackRows.push({
      id: element.dbId,
      reportId: element.reportId,
      title: element.name || element.programName,
      donorName: element.donorName,
      organizationName: element.accountName || undefined,
      dueDate: element.endDate,
      lastUpdated: element.updatedAt || undefined,
      stage: element.stage,
      status: element.status,
      year: element.year || undefined,
      portfolioName: element.portfolioName || '',
      totalDonations: element.totalDonations,
      totalCost: element.totalCost,
      accountName: element.accountName || '',
      additionalBudgetNotes: null,
      budget: element.budget,
      budgetNote1: element.budgetNote1 || null,
      budgetNote1Date: element.budgetNote1Date || null,
      budgetNote2: element.budgetNote2 || null,
      budgetNote2Date: element.budgetNote2Date || null,
      claim: element.claim,
      cnOptedIn: element.cnOptedIn || false,
      contactDonor: element.contactDonor ? String(element.contactDonor) : '',
      contactNpo:
        typeof element.contactNpo === 'string' ? [element.contactNpo] : [],
      dbId: element.dbId,
      errors: element.errors,
      fiscalYearEnd: element.fiscalYearEnd,
      fiscalYearStart: element.fiscalYearStart,
      feedbacks: [],
      createdAt: element.updatedAt || now,
      directCashVariance: 0,
      directInkindVariance: 0,
      startDate: element.startDate,
      endDate: element.endDate,
      feedbackType: element.feedbackType,
      indirectCashVariance: 0,
      indirectInkindVariance: 0,
      investmentType: element.investmentType,
      isAllocated: false,
      isV3: !element?.version || String(element.version).toLowerCase() === 'v3',
      name: element.name,
      npoName: element.npoName || '',
      programId: element.programId,
      programName: element.programName || '',
      relationshipManagerEmail: element.relationshipManagerEmail || null,
      relationshipManagerFirstName:
        element.relationshipManagerFirstName || null,
      relationshipManagerId: element.relationshipManagerId?.toString() || null,
      relationshipManagerLastName: element.relationshipManagerLastName || null,
      sponsorName: null,
      tasks: '',
      totalBudgetInitial: 0,
      totalBudgetVariance: 0,
      updatedAt: element.updatedAt || now,
      version: element.version,
      warnings: 0,
      reportingType: element.reportingType || 'FULL',
      optedInFullReporting: element.optedInFullReporting || false,
      contacts: getFeedbackContacts(
        element.feedbacks,
        element.contactDonor,
        element.contactNpo,
        element.relationshipManagerEmail,
      ),
    });
  });
  return {
    feedbackRows,
  };
};

type FeedbackApiFilter =
  | 'name'
  | 'program'
  | 'npo'
  | 'donor'
  | 'portfolio'
  | 'relationshipManagerId'
  | 'relationshipManagerEmail'
  | 'status'
  | 'dueDate'
  | 'createdAt';

type FeedbackApiFilters =
  | {
      or: {
        [k: string]: string;
      }[];
    }
  | {
      [k: string]: string;
    };

const date = new Date();
const currentYear = date.getFullYear();
const currentMonth = date.getMonth();
const currentDate = date.getDate();

const calculateDueDate = (dueDate: string): string => {
  switch (dueDate) {
    case 'Less than 1 day':
      return new Date(currentYear, currentMonth, currentDate - 1)
        .toISOString()
        .split('T')[0];
    case 'Less than 7 days':
      return new Date(currentYear, currentMonth, currentDate - 7)
        .toISOString()
        .split('T')[0];
    case 'Less than 1 month':
      return new Date(currentYear, currentMonth - 1, currentDate)
        .toISOString()
        .split('T')[0];
    case 'Less than 1 year':
      return new Date(currentYear - 1, currentMonth, currentDate)
        .toISOString()
        .split('T')[0];
    case 'Greater than 1 year':
      return new Date(currentYear - 2, currentMonth, currentDate)
        .toISOString()
        .split('T')[0];

    default:
      return new Date().toISOString().split('T')[0];
  }
};

const getIndividualFilters = (
  filters: FeedbackFilters,
  filterKey: string,
  getValue: (f: FeedbackFilter) => string,
): FeedbackApiFilters | undefined => {
  const result: {
    [k: string]: string;
  }[] = [];

  Object.keys(filters).forEach(d => {
    const element = filters[d];
    if (element.selected) {
      result.push({
        [filterKey]: getValue(element),
      });
    }
  });

  return result.length === 0
    ? undefined
    : {
        or: result,
      };
};

const defaultGetFilterValue = (f: FeedbackFilter) => f.name;
const defaultGetFilterDateValue = (f: FeedbackFilter) =>
  calculateDueDate(f.name);

const getSelectedFilters = ({
  donorNameFilters,
  cyclesFilters,
  dueDateFilters,
  portfolioNameFilters,
  relationshipFilters,
  staffFilters,
  statusFilters,
  charityNavigatorFilters,
  yearFilters,
}: ApplyFilters): FeedbackApiFilters[] => {
  const result: FeedbackApiFilters[] = [];

  const donorNames = getIndividualFilters(
    donorNameFilters,
    'donor',
    defaultGetFilterValue,
  );
  const feedbackCycles = getIndividualFilters(
    cyclesFilters,
    'feedbackCycleType',
    defaultGetFilterValue,
  );
  const dueDates = getIndividualFilters(
    dueDateFilters,
    'dueDate',
    defaultGetFilterDateValue,
  );
  const portfolios = getIndividualFilters(
    portfolioNameFilters,
    'portfolio',
    defaultGetFilterValue,
  );
  const managers = getIndividualFilters(
    relationshipFilters,
    'relationshipManagerId',
    f => f.value ?? '',
  );
  const staff = getIndividualFilters(
    staffFilters,
    'trueImpactStaff',
    defaultGetFilterValue,
  );
  const statuses = getIndividualFilters(
    statusFilters,
    'status',
    defaultGetFilterValue,
  );
  const charityNavigator = getIndividualFilters(
    charityNavigatorFilters,
    'cnOptedIn',
    () => 'true',
  );
  const years = getIndividualFilters(
    yearFilters,
    'year',
    defaultGetFilterValue,
  );

  donorNames && result.push(donorNames);
  feedbackCycles && result.push(feedbackCycles);
  dueDates && result.push(dueDates);
  years && result.push(years);
  portfolios && result.push(portfolios);
  managers && result.push(managers);
  staff && result.push(staff);
  statuses && result.push(statuses);
  charityNavigator && result.push(charityNavigator);

  return result;
};

export const getSelectedSort = (sortBy: FeedbackFilters): SortBy => {
  for (const key in sortBy) {
    if (Object.prototype.hasOwnProperty.call(sortBy, key)) {
      const status = sortBy[key];
      if (status.selected) {
        return (status.value as SortBy) || 'NAME_ASC';
      }
    }
  }
  return 'NAME_ASC';
};

const FEEDBACK_COUNT = 10;
export const getFeedback = (
  client: ApolloClient<object>,
  page: number | undefined,
  sortBy: SortBy | undefined,
  reportId: number | string | undefined,
  searchTerm: string | undefined,
  filters: ApplyFilters | undefined,
) => {
  const updatedFilters = filters ? getSelectedFilters(filters) : [];
  if (searchTerm) {
    updatedFilters.push({
      search: searchTerm,
    });
  }
  return client.query({
    query: query(reportId),
    variables: {
      top: FEEDBACK_COUNT,
      offset: page ? (page - 1) * FEEDBACK_COUNT : undefined,
      sortBy,
      reportId,
      filters: [{ and: updatedFilters }],
    },
    fetchPolicy: 'network-only',
  });
};

type SortBy =
  | 'UPDATED_AT_ASC'
  | 'UPDATED_AT_DESC'
  | 'ENABLED_ASC'
  | 'ENABLED_DESC'
  | 'ACCOUNT_ID_ASC'
  | 'ACCOUNT_ID_DESC'
  | 'PROGRAM_MANAGER_ID_ASC'
  | 'PROGRAM_MANAGER_ID_DESC'
  | 'PROGRAM_ID_ASC'
  | 'PROGRAM_ID_DESC'
  | 'NAME_ASC'
  | 'NAME_DESC'
  | 'STAGE_ASC'
  | 'STAGE_DESC'
  | 'STATUS_ASC'
  | 'STATUS_DESC'
  | 'UNVERIFIED_ASC'
  | 'UNVERIFIED_DESC'
  | 'START_DATE_ASC'
  | 'START_DATE_DESC'
  | 'END_DATE_ASC'
  | 'END_DATE_DESC'
  | 'REPORT_DESCRIPTION_ASC'
  | 'REPORT_DESCRIPTION_DESC'
  | 'SUCCESS_STORY_ASC'
  | 'SUCCESS_STORY_DESC'
  | 'LESSON_LEARNED_ASC'
  | 'LESSON_LEARNED_DESC'
  | 'COST_CURRENT_DIRECT_CASH_ASC'
  | 'COST_CURRENT_DIRECT_CASH_DESC'
  | 'COST_CURRENT_DIRECT_IN_KIND_ASC'
  | 'COST_CURRENT_DIRECT_IN_KIND_DESC'
  | 'COST_CURRENT_INDIRECT_CASH_ASC'
  | 'COST_CURRENT_INDIRECT_CASH_DESC'
  | 'COST_CURRENT_INDIRECT_IN_KIND_ASC'
  | 'COST_CURRENT_INDIRECT_IN_KIND_DESC'
  | 'COST_INITIAL_DIRECT_CASH_ASC'
  | 'COST_INITIAL_DIRECT_CASH_DESC'
  | 'COST_INITIAL_DIRECT_IN_KIND_ASC'
  | 'COST_INITIAL_DIRECT_IN_KIND_DESC'
  | 'COST_INITIAL_INDIRECT_CASH_ASC'
  | 'COST_INITIAL_INDIRECT_CASH_DESC'
  | 'COST_INITIAL_INDIRECT_IN_KIND_ASC'
  | 'COST_INITIAL_INDIRECT_IN_KIND_DESC'
  | 'COST_COMMENT_DIRECT_CASH_ASC'
  | 'COST_COMMENT_DIRECT_CASH_DESC'
  | 'COST_COMMENT_DIRECT_IN_KIND_ASC'
  | 'COST_COMMENT_DIRECT_IN_KIND_DESC'
  | 'COST_COMMENT_INDIRECT_CASH_ASC'
  | 'COST_COMMENT_INDIRECT_CASH_DESC'
  | 'COST_COMMENT_INDIRECT_IN_KIND_ASC'
  | 'COST_COMMENT_INDIRECT_IN_KIND_DESC'
  | 'BENEFICIARY_DESCRIPTION_ASC'
  | 'BENEFICIARY_DESCRIPTION_DESC'
  | 'LOGO_FILE_ID_ASC'
  | 'LOGO_FILE_ID_DESC'
  | 'STORY_FILE_ID_ASC'
  | 'STORY_FILE_ID_DESC'
  | 'SELECTED_SERVICE_ID_ASC'
  | 'SELECTED_SERVICE_ID_DESC'
  | 'NOTIFICATION_DT_ASC'
  | 'NOTIFICATION_DT_DESC'
  | 'TAXONOMY_SUBJECT_ID_ASC'
  | 'TAXONOMY_SUBJECT_ID_DESC'
  | 'TAXONOMY_SUBJECT_LEVEL_ASC'
  | 'TAXONOMY_SUBJECT_LEVEL_DESC'
  | 'PRIMARY_INTERVENTION_TYPE_ASC'
  | 'PRIMARY_INTERVENTION_TYPE_DESC'
  | 'PRIMARY_INTERVENTION_SUBTYPES_ASC'
  | 'PRIMARY_INTERVENTION_SUBTYPES_DESC'
  | 'PRIMARY_INTERVENTION_DESCRIPTION_ASC'
  | 'PRIMARY_INTERVENTION_DESCRIPTION_DESC'
  | 'INTERVENTION_DOSAGE_LEVEL_ASC'
  | 'INTERVENTION_DOSAGE_LEVEL_DESC'
  | 'INTERVENTION_DOSAGE_NUMBER_ASC'
  | 'INTERVENTION_DOSAGE_NUMBER_DESC'
  | 'YEAR_ASC'
  | 'YEAR_DESC'
  | 'CUSTOM_QUESTION_RESPONSES_ASC'
  | 'CUSTOM_QUESTION_RESPONSES_DESC';
